import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  Button,
  Form,
  Table,
  InputGroup,
  FormControl,
  Spinner,
  Nav,
  Tab,
} from 'react-bootstrap'
import apiServices from '../services/apiServices'

const mediaList = ['facebook', 'criteo', 'linkedin', 'twitter', 'google']

function UpdateModal({ setting }) {
  const {
    show,
    onHide,
    onSubmit,
    mediaOptions,
    accountOptions,
    defaultMedia,
    defaultAccount,
  } = setting
  const [media, setMedia] = useState(defaultMedia || 'facebook')
  const [account, setAccount] = useState(defaultAccount || '')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')

  useEffect(() => {
    setMedia(defaultMedia || 'facebook')
    setAccount(defaultAccount || '')
    setSince('')
    setUntil('')
  }, [defaultMedia, defaultAccount, show])

  const handleSubmit = () => {
    onSubmit({ media, account, since, until })
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>更新數據</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="formMedia">
            <Form.Label>媒體</Form.Label>
            <Form.Control
              as="select"
              value={media}
              onChange={(e) => setMedia(e.target.value)}
            >
              {mediaOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group controlId="formAccount" className="mt-3">
            <Form.Label>帳戶 (選填)</Form.Label>
            <Form.Control
              as="select"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
            >
              <option value="">全部帳戶</option>
              {accountOptions.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.account_name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group controlId="formSince" className="mt-3">
            <Form.Label>開始日期</Form.Label>
            <Form.Control
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
            />
          </Form.Group>
          <Form.Group controlId="formUntil" className="mt-3">
            <Form.Label>結束日期</Form.Label>
            <Form.Control
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          取消
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          確認更新
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default function MediaManagementDashboard() {
  const [dataMap, setDataMap] = useState({})
  const [loadingMap, setLoadingMap] = useState({})
  const [search, setSearch] = useState('')

  const [modalShow, setModalShow] = useState(false)
  const [modalMedia, setModalMedia] = useState('facebook')
  const [modalAccount, setModalAccount] = useState('')

  // Fetch all media data
  const fetchData = async (media) => {
    setLoadingMap((m) => ({ ...m, [media]: true }))
    try {
      const res = await apiServices.data({
        path: 'billing/statistic',
        method: 'get',
        params: { media },
      })
      setDataMap((m) => ({ ...m, [media]: res }))
    } finally {
      setLoadingMap((m) => ({ ...m, [media]: false }))
    }
  }

  useEffect(() => {
    mediaList.forEach((m) => {
      fetchData(m)
    })
  }, [])

  const openModal = (media, accountId = '') => {
    setModalMedia(media)
    setModalAccount(accountId)
    setModalShow(true)
  }

  const handleUpdate = async ({ media, account, since, until }) => {
    await apiServices.data({
      path: 'billing/statistic',
      method: 'put',
      params: { media, account_id: account, since, until },
    })
    setModalShow(false)
    fetchData(media)
  }

  return (
    <div className="container mt-4">
      <h3>媒體帳戶管理</h3>
      <InputGroup className="mb-3">
        <FormControl
          placeholder="搜尋帳戶名稱"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button variant="outline-primary" onClick={() => openModal('facebook')}>
          更新資訊
        </Button>
      </InputGroup>

      <Tab.Container defaultActiveKey="facebook">
        <Nav variant="tabs">
          {mediaList.map((m) => (
            <Nav.Item key={m}>
              <Nav.Link eventKey={m}>{m}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <Tab.Content className="mt-3">
          {mediaList.map((media) => (
            <Tab.Pane key={media} eventKey={media}>
              {loadingMap[media] ? (
                <Spinner animation="border" />
              ) : (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>帳戶 ID</th>
                      <th>帳戶名稱</th>
                      <th>開始 (min)</th>
                      <th>結束 (max)</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dataMap[media] || [])
                      .filter((item) => item.account_name.includes(search))
                      .map((item) => (
                        <tr key={item.account_id}>
                          <td>{item.account_id}</td>
                          <td>{item.account_name}</td>
                          <td>{item.min_date}</td>
                          <td>{item.max_date}</td>
                          <td>
                            <Button
                              size="sm"
                              onClick={() => openModal(media, item.account_id)}
                            >
                              更新
                            </Button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              )}
            </Tab.Pane>
          ))}
        </Tab.Content>
      </Tab.Container>

      <UpdateModal
        setting={{
          show: modalShow,
          onHide: () => setModalShow(false),
          onSubmit: handleUpdate,
          mediaOptions: mediaList,
          accountOptions: dataMap[modalMedia] || [],
          defaultMedia: modalMedia,
          defaultAccount: modalAccount,
        }}
      />
    </div>
  )
}

UpdateModal.propTypes = {
  setting: PropTypes.shape().isRequired,
}
